import React, { useEffect, useRef } from 'react';
import {
  Button,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  OverlayDrawer,
  useRestoreFocusSource,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import useStatHeaderStyles from '../StatHeader.styles';

interface IStatHeaderPanelProps {
  open: boolean;
  title: string;
  /**
   * Identifies which panel's content is showing. Changing it while the drawer is open moves
   * focus into the drawer, the same way opening it does.
   */
  contentKey: string;
  onClose: () => void;
  children: React.ReactNode;
}

/**
 * Shared chrome for the header drawers (profile, notifications, help).
 *
 * `OverlayDrawer` supplies the focus trap, the Escape handler, the light-dismiss overlay
 * and focus restoration to the trigger, so none of that is hand-rolled here. The drawer
 * widens to the full viewport below the small breakpoint via CSS only.
 */
const StatHeaderPanel = ({
  open,
  title,
  contentKey,
  onClose,
  children,
}: IStatHeaderPanelProps): JSX.Element => {
  const styles = useStatHeaderStyles();
  // Pairs with `useRestoreFocusTarget()` on the trigger buttons so Tabster returns focus to
  // the button that opened the drawer once it closes.
  const restoreFocusSourceAttributes = useRestoreFocusSource();
  const surfaceRef = useRef<HTMLDivElement>(null);

  /**
   * One drawer instance serves all three panels, so switching panels does not remount it and
   * Fluent's open-time focus move never re-runs — focus would stay on the header button that
   * was clicked. That matters beyond ergonomics: Tabster's modalizer marks a modal whose
   * focus sits outside it `aria-hidden="true"`, which drops the whole drawer out of the
   * accessibility tree while it is plainly visible on screen.
   */
  useEffect(() => {
    if (open) {
      surfaceRef.current?.focus();
    }
  }, [open, contentKey]);

  return (
    <OverlayDrawer
      {...restoreFocusSourceAttributes}
      ref={surfaceRef}
      className={styles.drawer}
      modalType="modal"
      size="small"
      position="end"
      open={open}
      onOpenChange={(_event, data) => {
        if (!data.open) {
          onClose();
        }
      }}
    >
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <Button
              appearance="subtle"
              aria-label="Close"
              icon={<Dismiss24Regular />}
              onClick={() => {
                // Move focus out of the drawer while it is still mounted. Tabster's restorer
                // reacts to focus leaving the drawer; removing a focused node does not
                // reliably raise `focusout`, so without this the trigger never gets focus
                // back and it lands on `<body>` instead. `document.activeElement` is used
                // rather than the button itself because not every browser focuses a button
                // when it is clicked (Safari does not).
                const focused = document.activeElement;
                if (focused instanceof HTMLElement) {
                  focused.blur();
                }
                onClose();
              }}
            />
          }
        >
          {title}
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody>{children}</DrawerBody>
    </OverlayDrawer>
  );
};

export default StatHeaderPanel;
